import type { ClientHomePlanHydrated } from "api-types/homePlans/homePlanResponses";
import type { ClientLocationPlanHydrated } from "api-types/locationPlans/locationPlansResponses";
import type { ClientMasterPlanHydrated } from "api-types/masterPlans/masterPlansResponses";
import type { PlanSetTemplateIdentifier } from "building-types/views/planSets";
import type { AnalyticsEventMetadata } from "lib/analytics";
import { sendAnalyticsEvent } from "lib/analytics";
import { addRumAction } from "lib/datadogRum";
import { useUserContext } from "lib/state/UserContextProvider";
import { isExportPlanFailureEvent, type ExportPlanEvent } from "./exportPlanTypes";
import type { LocationWithHeirarchy } from "./locationUtils";

const CONFIG_PRODUCT_NAME = "config";

const EXPORT_PLAN_RUM_ACTION = "planSet.exported";
const EXPORT_PLAN_FAILED_RUM_ACTION = "planSet.exportFailed";

function addExportPlanRumAction(eventName: ExportPlanEvent, planType: string, params: AnalyticsEventMetadata): void {
  const durationSeconds: number | undefined = params.export_duration_seconds;
  const failed = isExportPlanFailureEvent(eventName);

  addRumAction(failed ? EXPORT_PLAN_FAILED_RUM_ACTION : EXPORT_PLAN_RUM_ACTION, {
    event: eventName,
    source: CONFIG_PRODUCT_NAME,
    planType,
    durationMs: durationSeconds === undefined ? undefined : durationSeconds * 1000,
    documentId: params.document_id,
    planSetTemplateType: params.plan_set_template_type,
    planSetTemplateId: params.plan_set_template_id,
    planSetTemplateConfigId: params.plan_set_template_config_id,
    builderId: params.builder_id,
    // Only set on failures, where the download initializer reports why the export stopped.
    errorType: params.error_type,
    errorMessage: params.error_message,
  });
}

export function useExportMasterPlanEvents(
  masterPlan?: Partial<ClientMasterPlanHydrated>,
  planSetTemplateIdentifier?: PlanSetTemplateIdentifier,
) {
  const { builder } = useUserContext();

  return (eventName: ExportPlanEvent, metadata: AnalyticsEventMetadata = {}) => {
    const params = {
      builder_id: builder?.id,
      builder_name: builder?.companyName,
      plan_set_template_type: planSetTemplateIdentifier?.type,
      plan_set_template_id:
        planSetTemplateIdentifier?.type === "BUILT_IN" ? undefined : planSetTemplateIdentifier?.templateId,
      plan_set_template_config_id:
        planSetTemplateIdentifier?.type === "BUILT_IN" ? undefined : planSetTemplateIdentifier?.templateConfigId,
      product: CONFIG_PRODUCT_NAME,
      plan_type: "master_plan",
      plan_id: masterPlan?.id,
      plan_name: masterPlan?.name,
      detached: masterPlan?.detached,
      ...metadata,
    };
    sendAnalyticsEvent(eventName, params);
    addExportPlanRumAction(eventName, "master_plan", params);
  };
}

export function useExportCommunityPlanEvents(
  communityPlan?: Partial<ClientLocationPlanHydrated>,
  planSetTemplateIdentifier?: PlanSetTemplateIdentifier,
) {
  const { builder } = useUserContext();

  return (eventName: ExportPlanEvent, metadata: AnalyticsEventMetadata = {}) => {
    const params = {
      builder_id: builder?.id,
      builder_name: builder?.companyName,
      plan_set_template_type: planSetTemplateIdentifier?.type,
      plan_set_template_id:
        planSetTemplateIdentifier?.type === "BUILT_IN" ? undefined : planSetTemplateIdentifier?.templateId,
      plan_set_template_config_id:
        planSetTemplateIdentifier?.type === "BUILT_IN" ? undefined : planSetTemplateIdentifier?.templateConfigId,
      product: CONFIG_PRODUCT_NAME,
      master_plan_id: communityPlan?.masterPlanId,
      location_id: communityPlan?.locationId,
      plan_type: "community_plan",
      plan_id: communityPlan?.id,
      plan_name: communityPlan?.name,
      detached: communityPlan?.detached,
      ...metadata,
    };
    sendAnalyticsEvent(eventName, params);
    addExportPlanRumAction(eventName, "community_plan", params);
  };
}

function getLocationHierarchyString(locationHierarchy: LocationWithHeirarchy): string {
  const ancestors = locationHierarchy.ancestors.map((ancestor) => ancestor.name);
  ancestors.push(locationHierarchy.location.name);
  return ancestors.join("/");
}

export function useExportHomePlanEvents(
  homePlan?: Partial<ClientHomePlanHydrated>,
  planSetTemplateIdentifier?: PlanSetTemplateIdentifier,
  locationHierarchy?: LocationWithHeirarchy,
) {
  const { builder } = useUserContext();

  return (eventName: ExportPlanEvent, metadata: AnalyticsEventMetadata = {}) => {
    const params = {
      builder_id: builder?.id,
      builder_name: builder?.companyName,
      plan_set_template_type: planSetTemplateIdentifier?.type,
      plan_set_template_id:
        planSetTemplateIdentifier?.type === "BUILT_IN" ? undefined : planSetTemplateIdentifier?.templateId,
      plan_set_template_config_id:
        planSetTemplateIdentifier?.type === "BUILT_IN" ? undefined : planSetTemplateIdentifier?.templateConfigId,
      product: CONFIG_PRODUCT_NAME,
      home_plan_id: homePlan?.id,
      home_plan_name: homePlan?.planName,
      home_plan_status: homePlan?.planStatus,
      home_plan_category: homePlan?.category,
      home_plan_sales_agent_email: homePlan?.salesStaff?.email,
      home_plan_community_plan_id: homePlan?.communityPlanId,
      master_plan: homePlan?.masterPlanName,
      lot_id: homePlan?.lotId,
      lot_number: homePlan?.lot?.lotNumber,
      location_name: homePlan?.communityName,
      location_hierarchy: locationHierarchy ? getLocationHierarchyString(locationHierarchy) : undefined,
      detached: !!homePlan?.detachedAt,
      ...metadata,
    };
    sendAnalyticsEvent(eventName, params);
    addExportPlanRumAction(eventName, "home_plan", params);
  };
}

export function useExportSalesBrochureEvents(
  homePlan?: Partial<ClientHomePlanHydrated>,
  locationHierarchy?: LocationWithHeirarchy,
) {
  const { builder } = useUserContext();

  return (eventName: ExportPlanEvent, metadata: AnalyticsEventMetadata = {}) => {
    const params = {
      builder_id: builder?.id,
      builder_name: builder?.companyName,
      product: CONFIG_PRODUCT_NAME,
      home_plan_id: homePlan?.id,
      home_plan_name: homePlan?.planName,
      home_plan_status: homePlan?.planStatus,
      home_plan_category: homePlan?.category,
      home_plan_sales_agent_email: homePlan?.salesStaff?.email,
      home_plan_community_plan_id: homePlan?.communityPlanId,
      master_plan: homePlan?.masterPlanName,
      lot_id: homePlan?.lotId,
      lot_number: homePlan?.lot?.lotNumber,
      location_name: homePlan?.communityName,
      location_hierarchy: locationHierarchy ? getLocationHierarchyString(locationHierarchy) : undefined,
      detached: !!homePlan?.detachedAt,
      ...metadata,
    };
    sendAnalyticsEvent(eventName, params);
    addExportPlanRumAction(eventName, "sales_brochure", params);
  };
}
